import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { notices } from '../data/notices';
import SubLayout from '../components/layout/SubLayout';
import { customerMenus } from '../data/customerMenus';

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-top: 2px solid ${props => props.theme.colors.primary};

  @media (max-width: 768px) {
    display: none;
  }
`;

const Th = styled.th`
  padding: 15px;
  background-color: ${props => props.theme.colors.lightGray};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  font-weight: 600;
  text-align: center;
`;

const Td = styled.td`
  padding: 15px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  text-align: ${props => props.align || 'center'};
`;

const ImportantBadge = styled.span`
  background-color: ${props => props.theme.colors.important};
  color: white;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 12px;
  margin-right: 8px;
`;

const ImportantLabel = styled.span`
  color: ${props => props.theme.colors.important};
  font-weight: 800;
`;

const TableRow = styled.tr`
  font-weight: ${props => props.isImportant ? '700' : '400'};
  background-color: ${props => props.isImportant ? '#fafafa' : 'transparent'};
  &:hover { background-color: #f5f5f5; }
`;

/* 모바일 카드 리스트 */
const MobileList = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    border-top: 2px solid ${props => props.theme.colors.primary};
  }
`;

const MobileItem = styled.div`
  padding: 15px;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  cursor: pointer;
  background: ${props => props.isImportant ? '#fafafa' : 'white'};
  &:hover { background: #f5f5f5; }
`;

const MobileTitle = styled.div`
  font-weight: ${props => props.isImportant ? '700' : '400'};
  margin-bottom: 6px;
`;

const MobileMeta = styled.div`
  display: flex;
  gap: 12px;
  font-size: 13px;
  color: #888;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 40px;
`;

const PageButton = styled.button`
  padding: 8px 12px;
  border: 1px solid ${props => props.active ? props.theme.colors.primary : props.theme.colors.border};
  background-color: ${props => props.active ? props.theme.colors.primary : 'white'};
  color: ${props => props.active ? 'white' : props.theme.colors.textMain};
  cursor: pointer;
  border-radius: 4px;
  &:hover { background-color: ${props => props.active ? props.theme.colors.primary : '#f5f5f5'}; }
`;

const PAGE_SIZE = 10;

const NoticeList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentPage = parseInt(searchParams.get('page')) || 1;

  const importantNotices = notices.filter(n => n.is_important === 1);
  const normalNotices = notices.filter(n => n.is_important !== 1);

  const totalPages = Math.ceil(normalNotices.length / PAGE_SIZE);
  const pagedNormal = normalNotices.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const displayedNotices = currentPage === 1 ? [...importantNotices, ...pagedNormal] : pagedNormal;

  const handleClick = (id) => navigate(`/notices/${id}`, { state: { fromPage: currentPage } });

  return (
    <SubLayout
      title="공지사항"
      sidebarMenus={customerMenus}
      category="고객센터"
      categoryEn="Customer"
    >
      {/* 데스크탑 테이블 */}
      <Table>
        <thead>
          <tr>
            <Th style={{ width: '80px' }}>번호</Th>
            <Th>제목</Th>
            <Th style={{ width: '130px' }}>작성일</Th>
            <Th style={{ width: '100px' }}>조회수</Th>
          </tr>
        </thead>
        <tbody>
          {displayedNotices.map((notice, index) => (
            <TableRow key={notice.id} isImportant={notice.is_important === 1}>
              <Td>
                {notice.is_important === 1 ? (
                  <ImportantLabel>공지</ImportantLabel>
                ) : (
                  normalNotices.length - ((currentPage - 1) * PAGE_SIZE) - (index - importantNotices.length)
                )}
              </Td>
              <Td align="left" style={{ cursor: 'pointer' }} onClick={() => handleClick(notice.id)}>
                {notice.is_important === 1 && <ImportantBadge>공지</ImportantBadge>}
                {notice.title}
              </Td>
              <Td>{new Date(notice.created_at).toLocaleDateString()}</Td>
              <Td>{notice.view_count}</Td>
            </TableRow>
          ))}
        </tbody>
      </Table>

      {/* 모바일 카드 리스트 */}
      <MobileList>
        {displayedNotices.map(notice => (
          <MobileItem
            key={notice.id}
            isImportant={notice.is_important === 1}
            onClick={() => handleClick(notice.id)}
          >
            <MobileTitle isImportant={notice.is_important === 1}>
              {notice.is_important === 1 && <ImportantBadge>공지</ImportantBadge>}
              {notice.title}
            </MobileTitle>
            <MobileMeta>
              <span>{new Date(notice.created_at).toLocaleDateString()}</span>
              <span>조회 {notice.view_count}</span>
            </MobileMeta>
          </MobileItem>
        ))}
      </MobileList>

      {totalPages > 1 && (
        <PaginationWrapper>
          {Array.from({ length: totalPages }, (_, i) => (
            <PageButton
              key={i + 1}
              active={currentPage === i + 1}
              onClick={() => setSearchParams({ page: i + 1 })}
            >
              {i + 1}
            </PageButton>
          ))}
        </PaginationWrapper>
      )}
    </SubLayout>
  );
};

export default NoticeList;
